/**
 * ModernWash 소스 봉투(envelope) → 화면 표시 상태 계산 (순수 함수).
 *
 * 어댑터 응답 봉투와 axios 오류를 받아, 화면이 어떤 안내를 보여줄지 하나의
 * 서술자(descriptor)로 정규화한다. 5개 세차 화면이 각자 다른 문구/분기를 쓰지
 * 않도록 이 파일에 모았다. React 의존이 없어 단위 테스트 대상이다.
 *
 * 봉투(모든 응답 공통):
 *   { enabled, scope:{ role, storeId, storeName, linkedToModernwash },
 *     source:{ reachable, degraded, fetchedAt, error:{ code, message }|null }, data|items }
 *
 * 오류 코드:
 *   SOURCE_DISABLED | SOURCE_UNREACHABLE | SOURCE_AUTH_FAILED | SOURCE_UPSTREAM_ERROR | SOURCE_NO_DATA
 */

/** 화면 상태 코드 — 봉투/오류를 사람이 읽을 수 있는 상태로 좁힌 것 */
export const SOURCE_STATE = {
  OK: 'OK',
  PERMISSION: 'PERMISSION',
  DISABLED: 'DISABLED',
  UNLINKED: 'UNLINKED',
  UNREACHABLE: 'UNREACHABLE',
  AUTH_FAILED: 'AUTH_FAILED',
  UPSTREAM_ERROR: 'UPSTREAM_ERROR',
  NO_DATA: 'NO_DATA',
  ERROR: 'ERROR',
};

/**
 * 각 상태의 표시 규격.
 *   kind    'empty' = 큰 안내 카드(EmptyOperationalState), 'alert' = 상단 배너(Alert)
 *   variant EmptyOperationalState 변형 (kind='empty' 일 때)
 *   severity Alert 심각도 (kind='alert' 일 때)
 *   retryable "다시 시도" 버튼 노출 여부 (재조회로 상태가 바뀔 여지가 있을 때만 true)
 */
const PRESENTATION = {
  [SOURCE_STATE.DISABLED]: {
    kind: 'empty',
    variant: 'preparing',
    retryable: false,
    title: 'ModernWash 연동이 아직 켜지지 않았어요',
    description:
      '연동 준비가 끝나면 이 화면에서 세차 운영 데이터를 볼 수 있어요. (준비 중)',
  },
  [SOURCE_STATE.UNLINKED]: {
    kind: 'empty',
    variant: 'empty',
    retryable: false,
    title: '이 매장은 ModernWash 연동 매장이 아닙니다',
    description: 'ModernWash 원천과 연결된 매장에서만 세차 데이터가 표시됩니다.',
  },
  [SOURCE_STATE.NO_DATA]: {
    kind: 'empty',
    variant: 'empty',
    retryable: false,
    title: '이 항목의 원천 데이터가 아직 없습니다',
    description: '원천에서 아직 이 항목의 데이터를 제공하지 않아요.',
  },
  [SOURCE_STATE.PERMISSION]: {
    kind: 'empty',
    variant: 'denied',
    retryable: false,
    title: '이 화면을 볼 권한이 없습니다',
    description: '접근 권한이 필요해요. 매장 관리자에게 문의해 주세요.',
  },
  [SOURCE_STATE.UNREACHABLE]: {
    kind: 'alert',
    severity: 'error',
    retryable: true,
    title: 'ModernWash 원천에 연결할 수 없습니다 (일시 장애)',
    description: '잠시 후 다시 시도해 주세요. 계속되면 운영자에게 알려 주세요.',
  },
  [SOURCE_STATE.AUTH_FAILED]: {
    kind: 'alert',
    severity: 'warning',
    retryable: true,
    title: '원천 인증 오류 — 운영자 확인 필요',
    description: 'ModernWash 원천 인증에 실패했어요. 서비스 계정 설정 확인이 필요합니다.',
  },
  [SOURCE_STATE.UPSTREAM_ERROR]: {
    kind: 'alert',
    severity: 'error',
    retryable: true,
    title: '원천 서버 오류',
    description: 'ModernWash 원천 서버에서 오류가 발생했어요. 잠시 후 다시 시도해 주세요.',
  },
  [SOURCE_STATE.ERROR]: {
    kind: 'alert',
    severity: 'error',
    retryable: true,
    title: '데이터를 불러오지 못했어요',
    description: '네트워크 상태를 확인한 뒤 다시 시도해 주세요.',
  },
};

/** v1 오류 코드 → 화면 상태 코드 */
const ERROR_CODE_TO_STATE = {
  SOURCE_DISABLED: SOURCE_STATE.DISABLED,
  SOURCE_UNREACHABLE: SOURCE_STATE.UNREACHABLE,
  SOURCE_AUTH_FAILED: SOURCE_STATE.AUTH_FAILED,
  SOURCE_UPSTREAM_ERROR: SOURCE_STATE.UPSTREAM_ERROR,
  SOURCE_NO_DATA: SOURCE_STATE.NO_DATA,
};

const buildDescriptor = (code, extra = {}) => {
  const preset = PRESENTATION[code] || PRESENTATION[SOURCE_STATE.ERROR];
  return {
    code,
    // blocking = 데이터 표(table/카드) 대신 안내를 보여준다
    blocking: true,
    kind: preset.kind,
    variant: preset.variant || null,
    severity: preset.severity || 'info',
    retryable: Boolean(preset.retryable),
    title: preset.title,
    description: preset.description,
    degraded: false,
    fetchedAt: extra.fetchedAt || null,
  };
};

/**
 * 봉투/오류 → 화면 표시 서술자.
 *
 * 우선순위:
 *   1) 하드 오류(axios) — 403 은 권한 없음, 그 외는 일반 오류
 *   2) enabled === false            → 연동 미활성(준비 중)
 *   3) scope.linkedToModernwash === false → 비연동 매장 (200 + 빈 결과 + 사유)
 *   4) source.error.code            → 원천 오류 분류
 *   5) source.reachable === false   → (코드 없이도) 연결 불가로 간주
 *   6) 그 외                        → 정상. source.degraded 면 "응답 지연" 표시
 *
 * @param {object|null} response 어댑터 200 응답 봉투
 * @param {any} error axios 오류 (없으면 null)
 * @returns 표시 서술자 { code, blocking, kind, variant, severity, retryable, title, description, degraded, fetchedAt }
 */
export function computeSourceState({ response = null, error = null } = {}) {
  // 1) 하드 오류 (요청 자체가 실패)
  if (error) {
    const status = error?.response?.status;
    if (status === 403) {
      return buildDescriptor(SOURCE_STATE.PERMISSION);
    }
    const serverMessage = error?.response?.data?.message;
    const descriptor = buildDescriptor(SOURCE_STATE.ERROR);
    // 서버가 사유를 내려주면 안내 문구를 대체 (기본 문구보다 구체적)
    if (serverMessage) descriptor.description = serverMessage;
    return descriptor;
  }

  // 응답이 아직 없으면(초기/로딩) 중립 상태 — 화면은 로딩 스켈레톤을 그린다
  if (!response) {
    return {
      code: SOURCE_STATE.OK,
      blocking: false,
      kind: null,
      variant: null,
      severity: 'info',
      retryable: false,
      title: '',
      description: '',
      degraded: false,
      fetchedAt: null,
    };
  }

  const source = response.source || {};
  const fetchedAt = source.fetchedAt || null;
  // 봉투 최상위 reason — "정상이지만 데이터 없음"(비연동/원천 없음) 안내 문구. 장애가 아니다.
  const reason = typeof response.reason === 'string' && response.reason ? response.reason : null;

  // 2) 어댑터 자체가 꺼져 있음 (dark 배포 스위치 off)
  if (response.enabled === false) {
    return buildDescriptor(SOURCE_STATE.DISABLED, { fetchedAt });
  }

  // 3) 비연동 매장/선택 — 403 이 아니라 200 + 빈 결과 + 사유 문구
  if (response.scope && response.scope.linkedToModernwash === false) {
    const descriptor = buildDescriptor(SOURCE_STATE.UNLINKED, { fetchedAt });
    if (reason) descriptor.detail = reason;
    return descriptor;
  }

  // 4) 원천 오류 코드 분류
  const errorCode = source.error && source.error.code;
  if (errorCode) {
    const state = ERROR_CODE_TO_STATE[errorCode] || SOURCE_STATE.ERROR;
    const descriptor = buildDescriptor(state, { fetchedAt });
    // 원천이 구체적 사유를 주면 보조 설명으로 덧붙인다 (기본 문구는 유지)
    if (source.error.message) descriptor.detail = source.error.message;
    else if (reason) descriptor.detail = reason;
    return descriptor;
  }

  // 5) 코드는 없지만 원천 도달 실패로 표기된 경우
  if (source.reachable === false) {
    return buildDescriptor(SOURCE_STATE.UNREACHABLE, { fetchedAt });
  }

  // 6) 정상 — 데이터 표시. degraded 면 "응답 지연" 배지만 얹는다.
  return {
    code: SOURCE_STATE.OK,
    blocking: false,
    kind: null,
    variant: null,
    severity: 'info',
    retryable: false,
    title: '',
    description: '',
    degraded: Boolean(source.degraded),
    fetchedAt,
  };
}
